import React, { useState, useEffect, useCallback, useMemo } from "react";
import { TbUserPlus, TbBan, TbTrash, TbShieldCheck, TbShield } from "react-icons/tb";
import { api } from "../../lib/api";
import { useSort } from "../../hooks/useSort";
import { useOrganizations } from "../../hooks/useOrganizations";
import { fmtDate } from "../../lib/dates";
import { Button, Chip, Alert, EmptyState, SkeletonRows, SortHeader, Modal, Field, ConfirmModal } from "../../components";

// Admin panel, people and organizations.
//
// v1's AdminPanel is 1,973 lines covering five unrelated jobs. This screen is the two
// that belong under Organizations; branding, thresholds and integrations become
// Settings screens in a later pass.
//
// Worth knowing while reading this: there is no role model. `isadmin` is a single
// boolean, so "role" here is a two-state toggle, not a dropdown of roles that could
// exist.

const AdminUsersScreen = ({ me }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("s1/api/users");
      setUsers(Array.isArray(data) ? data : data?.users || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.username || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, search]);

  const { sort, toggleSort, sorted } = useSort(filtered, "username", ["created_at"]);

  const adminCount = users.filter((u) => u.isadmin === 1 && !u.banned).length;

  const toggleRole = async (u) => {
    // Guard the last admin. Demoting the only one locks everybody out of this screen,
    // and the recovery is a manual database edit.
    if (u.isadmin === 1 && adminCount <= 1) {
      setError("This is the only administrator. Promote someone else first.");
      return;
    }
    setBusyId(u.user_id);
    try {
      await api.put(`s1/api/users/role/${u.user_id}`);
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, isadmin: x.isadmin === 1 ? 0 : 1 } : x)));
      setNotice(`${u.username} is now ${u.isadmin === 1 ? "a standard user" : "an administrator"}.`);
    } catch (e) {
      setError(e.message || "Could not change the role.");
    } finally {
      setBusyId(null);
    }
  };

  const runConfirm = async () => {
    const { user, kind } = confirm;
    setBusyId(user.user_id);
    try {
      if (kind === "ban") {
        await api.del(`s1/api/users/${user.user_id}`);
        setUsers((prev) => prev.map((x) => (x.user_id === user.user_id ? { ...x, banned: 1 } : x)));
        setNotice(`${user.username} has been suspended.`);
      } else {
        await api.del(`s1/api/users/remove/${user.user_id}`);
        setUsers((prev) => prev.filter((x) => x.user_id !== user.user_id));
        setNotice(`${user.username} has been removed.`);
      }
      setConfirm(null);
    } catch (e) {
      setError(e.message || "That did not work.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>Admin panel</h2>
          <p>People in this workspace, and what they can reach.</p>
        </div>
        <div className="v2-pagehead-actions">
          <Chip><b>{sorted.length}</b> {sorted.length === 1 ? "person" : "people"}</Chip>
          <Button variant="primary" onClick={() => setShowCreate(true)}><TbUserPlus size={15} /> New user</Button>
        </div>
      </div>

      <div style={{ marginBottom: ".625rem" }}>
        <input
          className="v2-input" style={{ maxWidth: "22rem" }}
          type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search people" aria-label="Search people"
        />
      </div>

      <Alert>{error}</Alert>
      {notice && <Alert tone="success">{notice}</Alert>}

      <div className="v2-tablewrap">
        <table className="v2-table">
          <thead>
            <tr>
              <SortHeader label="Name" colKey="username" sort={sort} onSort={toggleSort} />
              <SortHeader label="Email" colKey="email" sort={sort} onSort={toggleSort} />
              <SortHeader label="Access" colKey="isadmin" sort={sort} onSort={toggleSort} />
              <SortHeader label="Status" colKey="banned" sort={sort} onSort={toggleSort} />
              <SortHeader label="Created" colKey="created_at" sort={sort} onSort={toggleSort} />
              <th className="v2-num">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={5} cols={6} />
            ) : sorted.length === 0 ? (
              <tr><td colSpan={6}><EmptyState title="Nobody here">{search ? `Nothing matches "${search}".` : "Invite someone to get started."}</EmptyState></td></tr>
            ) : (
              sorted.map((u) => {
                const isSelf = u.email === me?.email;
                const busy = busyId === u.user_id;
                return (
                  <tr key={u.user_id}>
                    <td>{u.username}{isSelf && <span className="v2-dim"> (You)</span>}</td>
                    <td className="v2-dim v2-mono">{u.email}</td>
                    <td>
                      <button type="button" className="v2-chip" style={{ cursor: "pointer" }} disabled={busy} onClick={() => toggleRole(u)}
                        title={u.isadmin === 1 ? "Demote to standard user" : "Promote to administrator"}>
                        {u.isadmin === 1 ? <><TbShieldCheck size={13} /> Administrator</> : <><TbShield size={13} /> Standard</>}
                      </button>
                    </td>
                    <td className={u.banned ? "" : "v2-dim"} style={u.banned ? { color: "var(--v2-danger)" } : undefined}>
                      {u.banned ? "Suspended" : "Active"}
                    </td>
                    <td className="v2-dim">{fmtDate(u.created_at)}</td>
                    <td className="v2-num">
                      <Button icon title="Suspend" disabled={busy || isSelf} onClick={() => setConfirm({ user: u, kind: "ban" })}>
                        <TbBan size={15} />
                      </Button>
                      <Button icon variant="danger" title="Remove permanently" disabled={busy || isSelf} onClick={() => setConfirm({ user: u, kind: "remove" })}>
                        <TbTrash size={15} />
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />}

      {confirm && (
        <ConfirmModal
          title={confirm.kind === "ban" ? "Suspend this account?" : "Remove this account?"}
          body={confirm.kind === "ban"
            ? `${confirm.user.username} will lose access but the account and its data are kept, so this can be undone.`
            : `${confirm.user.username} will be removed permanently. This cannot be undone.`}
          confirmLabel={confirm.kind === "ban" ? "Suspend" : "Remove"}
          busy={busyId === confirm.user.user_id}
          onClose={() => setConfirm(null)}
          onConfirm={runConfirm}
        />
      )}
    </div>
  );
};

const CreateUserModal = ({ onClose, onDone }) => {
  const [form, setForm] = useState({ username: "", email: "", password: "", userType: "user", organization_id: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // POST /s1/api/create_user requires organization_id — it is a NOT-NULL-ish column on
  // users, and the route rejects the request outright without it ("Incorrect
  // organization_id"). This dialog never collected it, so creating a user always failed
  // with a 400. v1's admin panel asks for it with exactly this dropdown.
  const { organizations, loading: orgsLoading } = useOrganizations();
  useEffect(() => {
    // Default to the first organization once the list lands, so the common case is one
    // fewer decision. Only when nothing is chosen yet — never overwrite a real choice.
    if (!form.organization_id && organizations.length) {
      setForm((f) => (f.organization_id ? f : { ...f, organization_id: String(organizations[0].id) }));
    }
  }, [organizations, form.organization_id]);

  const submit = async () => {
    if (!form.username.trim() || !form.email.trim() || !form.password) { setErr("Name, email and password are all required."); return; }
    if (!form.organization_id) { setErr("Choose which organization this user belongs to."); return; }
    setBusy(true); setErr("");
    try { await api.post("s1/api/create_user", form); onDone(); }
    catch (e) { setErr(e.message || "Could not create the user."); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="New user" onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create user"}</Button>
      </>}>
      <Alert>{err}</Alert>
      <Field label="Name"><input className="v2-input" value={form.username} onChange={set("username")} autoFocus /></Field>
      <Field label="Email"><input className="v2-input" type="email" value={form.email} onChange={set("email")} /></Field>
      <Field label="Temporary password" hint="They can change it from their profile after signing in.">
        <input className="v2-input" type="text" value={form.password} onChange={set("password")} />
      </Field>
      <Field label="Organization"
             hint={!orgsLoading && !organizations.length
               ? "This workspace has no organizations yet — create one before adding users."
               : "Which client record this account belongs to."}>
        <select className="v2-input" value={form.organization_id} onChange={set("organization_id")}
                disabled={orgsLoading || !organizations.length}>
          {orgsLoading && <option value="">Loading…</option>}
          {!orgsLoading && !organizations.length && <option value="">No organizations available</option>}
          {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </Field>
      <Field label="Access">
        <select className="v2-input" value={form.userType} onChange={set("userType")}>
          <option value="user">Standard</option>
          <option value="admin">Administrator</option>
        </select>
      </Field>
    </Modal>
  );
};

export default AdminUsersScreen;
